import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import {Button} from "primereact/button";
import DocumentSettings from "../../document/DocumentSettings";
import SidebarLeftMode from "../settings/SidebarLeftMode";
import {Outline} from "../../document/components/pdf/OutlineList";
import {pdfjs} from "react-pdf";
import {TextSearchGlobals} from "../../document/components/pdf/TextSearch";
import {Attachment, AttachmentsDocument} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../PortalContext";
import Immutable from "../../../shared/immutable/Immutable";
import Translate from "../../../shared/translation/Translate";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {ScreenSize} from "../../../shared/models/ScreenSize";
import SidebarRightMode from "../settings/SidebarRightMode";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import {PortalHighlightResult} from "../../document/TextContainer";
import HighlighterLanguage from "../../dialog/parameter/HighlighterLanguage";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";
import ViewerType from "../../document/DataType";

export interface LeftSidebarButtonsProperties extends TextSearchGlobals, I18nGlobals {
	context: PortalContext;
	portalDocument?: Immutable<PortalDocumentInterface>;
}

export interface LeftSidebarButtonsState {
	numberOfPages: number;
	outlineCount: number;
}

class LeftSidebarButtonsDefinition extends PortalPureComponent<LeftSidebarButtonsProperties, LeftSidebarButtonsState> {
	constructor(props: Readonly<LeftSidebarButtonsProperties>) {
		super(props);

		this.state = {
			numberOfPages: 0,
			outlineCount: 0
		}

		this.changeSidebarMode = this.changeSidebarMode.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.setNumberOfPages();
		await this.buildOutlineList();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<LeftSidebarButtonsProperties>, prevState: Readonly<LeftSidebarButtonsState>
	): Promise<void> {
		await this.setNumberOfPages();

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (this.props.portalDocument!.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument!.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.portalDocument!.get().settings.viewerSettings.currentPage !==
			prevProps.portalDocument?.get().settings.viewerSettings.currentPage) {
			await this.buildOutlineList();
		}
	}

	/**
	 * Change the sidebar left mode to global settings
	 */
	private changeSidebarMode(mode: SidebarLeftMode): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.sidebarLeftMode = viewerSettings.sidebarLeftMode === mode ? SidebarLeftMode.NONE : mode;

		if (window.document.documentElement.offsetWidth < ScreenSize.SM) {
			viewerSettings.sidebarRightMode = SidebarRightMode.NONE;
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let numberOfPages: number = 0;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined =
				portalDocument.getAttachment(FunctionHelper.applyValue(documentSettings.portfolioSelection, ""));

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	private countOutlineItems(items: Array<Outline>): number {
		let outlines: Array<Outline> = items;
		let count: number = outlines.length;

		for (let outline of outlines) {
			count += this.countOutlineItems(outline.items);
		}

		return count;
	}

	/**
	 * Build outline data for the tree list
	 */
	private async buildOutlineList(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		try {
			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let outlines: Array<Outline> = FunctionHelper.applyValue(await pdfDocument.getOutline(), []);
			let outlineCount: number = this.countOutlineItems(outlines);

			this.setState({
				outlineCount: outlineCount
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	private isPdf(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let isPdf: boolean = false;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				isPdf = portalDocument.getViewerType(attachment!) === ViewerType.PDF;
			}
		} else {
			isPdf = portalDocument.getViewerType() === ViewerType.PDF;
		}

		return isPdf;
	}

	private isHTML(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let isHTML: boolean = false;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				isHTML = portalDocument.getViewerType(attachment!) === ViewerType.HTML;
			}
		} else {
			isHTML = portalDocument.getViewerType() === ViewerType.HTML;
		}

		return isHTML;
	}

	private isText(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let isText: boolean = false;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				isText = portalDocument.getViewerType(attachment!) === ViewerType.TEXT;
			}
		} else {
			isText = portalDocument.getViewerType() === ViewerType.TEXT;
		}

		return isText;
	}

	private isMarkdown(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerData: PortalHighlightResult | undefined = portalDocument.data;
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		return viewerSettings.hightlighterLanguage === HighlighterLanguage.MARKDOWN || (
			viewerSettings.hightlighterLanguage === HighlighterLanguage.AUTO &&
			viewerData?.language === HighlighterLanguage.MARKDOWN
		);
	}

	private renderHTMLButtons(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		if (!this.isHTML()) {
			return null;
		}

		let tooltip: string;
		let icons: Array<string> = ["icon"];
		if (viewerSettings.viewAsHTML) {
			tooltip = Translate.value("viewerscreen.toolbars.view_as_text", {ns: "screen"});
			icons.push("icon-text");
		} else {
			tooltip = Translate.value("viewerscreen.toolbars.view_as_html", {ns: "screen"});
			icons.push("icon-code");
		}

		return (
			<>
				<Button icon={icons.join(" ")} tooltip={tooltip}
						onClick={() => {
							viewerSettings.viewAsHTML = !viewerSettings.viewAsHTML;
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
				{!viewerSettings.viewAsHTML &&
					<Button className={viewerSettings.textWrap ? "active" : ""} icon="icon icon-list wrap"
							tooltip={Translate.value("viewerscreen.toolbars.wrap", {ns: "screen"})}
							onClick={() => {
								viewerSettings.textWrap = !viewerSettings.textWrap;
								this.props.context.documentManager.updateGlobalDocumentList();
							}}/>
				}
			</>
		);
	}

	private renderTextButtons(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		if (!this.isText() || this.isMarkdown()) {
			return null;
		}

		return (
			<>
				<Button className={viewerSettings.textWrap ? "active" : ""} icon="icon icon-list wrap"
						tooltip={Translate.value("viewerscreen.toolbars.wrap", {ns: "screen"})}
						onClick={() => {
							viewerSettings.textWrap = !viewerSettings.textWrap;
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
			</>
		);
	}

	private renderMarkdownButtons(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		if (!this.isMarkdown()) {
			return null;
		}

		let tooltip: string;
		let icons: Array<string> = ["icon"];
		if (viewerSettings.viewAsMarkdown) {
			tooltip = Translate.value("viewerscreen.toolbars.view_as_text", {ns: "screen"});
			icons.push("icon-text");
		} else {
			tooltip = Translate.value("viewerscreen.toolbars.view_as_markdown", {ns: "screen"});
			icons.push("icon-code");
		}

		return (
			<>
				<Button icon={icons.join(" ")} tooltip={tooltip}
						onClick={() => {
							viewerSettings.viewAsMarkdown = !viewerSettings.viewAsMarkdown;
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
				{!viewerSettings.viewAsMarkdown &&
					<Button className={viewerSettings.textWrap ? "active" : ""} icon="icon icon-list wrap"
							tooltip={Translate.value("viewerscreen.toolbars.wrap", {ns: "screen"})}
							onClick={() => {
								viewerSettings.textWrap = !viewerSettings.textWrap;
								this.props.context.documentManager.updateGlobalDocumentList();
							}}/>
				}
			</>
		);
	}

	private renderPDFButtons(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (!portalDocument.isViewable() || !this.isPdf()) {
			return null;
		}

		if (portalDocument.isPortfolio() && !portalDocument.isPortfolioFile()) {
			return null;
		}

		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);
		let searchResultCount: number = FunctionHelper.applyValue(
			this.props.textSearchResults?.get().length, 0
		);

		let signatureCount: number = portalDocument.signatureAnnotations.length;
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		return (
			<>
				<Button
					className={viewerSettings.sidebarLeftMode === SidebarLeftMode.PAGES ? "active" : ""}
					onClick={() => this.changeSidebarMode(SidebarLeftMode.PAGES)} icon="icon icon-pages"
					badge={this.state.numberOfPages > 0 ? this.state.numberOfPages.toString() : undefined}
					tooltip={Translate.value("viewerscreen.toolbars.pages", {ns: "screen"})}/>
				{!portalDocument.isPortfolio() &&
					<>
						<Button
							className={viewerSettings.sidebarLeftMode === SidebarLeftMode.ATTACHMENTS ? "active" : ""}
							onClick={() => this.changeSidebarMode(SidebarLeftMode.ATTACHMENTS)}
							badge={attachments.length > 0 ? attachments.length.toString() : undefined}
							icon="icon icon-attachment small"
							tooltip={Translate.value("viewerscreen.toolbars.attachments", {ns: "screen"})}/>
						<Button
							className={viewerSettings.sidebarLeftMode === SidebarLeftMode.OUTLINES ? "active" : ""}
							onClick={() => this.changeSidebarMode(SidebarLeftMode.OUTLINES)}
							badge={this.state.outlineCount > 0 ? this.state.outlineCount.toString() : undefined}
							icon="icon icon-bookmark"
							tooltip={Translate.value("viewerscreen.toolbars.outlines", {ns: "screen"})}/>
						<Button
							className={viewerSettings.sidebarLeftMode === SidebarLeftMode.SIGNATURES ? "active" : ""}
							onClick={() => this.changeSidebarMode(SidebarLeftMode.SIGNATURES)}
							badge={signatureCount > 0 ? signatureCount.toString() : undefined}
							icon="icon icon-signature small"
							tooltip={Translate.value("viewerscreen.toolbars.signatures", {ns: "screen"})}/>
						<Button
							className={viewerSettings.sidebarLeftMode === SidebarLeftMode.SEARCH ? "active" : ""}
							onClick={() => this.changeSidebarMode(SidebarLeftMode.SEARCH)}
							badge={searchResultCount > 0 ? searchResultCount.toString() : undefined}
							icon="icon icon-search"
							tooltip={Translate.value("viewerscreen.toolbars.search", {ns: "screen"})}/>
					</>
				}
			</>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<>
				{this.renderPDFButtons()}
				{this.renderTextButtons()}
				{this.renderHTMLButtons()}
				{this.renderMarkdownButtons()}
			</>
		)
	}
}

const LeftSidebarButtons = React.memo(LeftSidebarButtonsDefinition,
	(
		prevProps: Readonly<LeftSidebarButtonsProperties>,
		nextProps: Readonly<LeftSidebarButtonsProperties>
	) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		return true;
	}
);

export default LeftSidebarButtons;